import { buildSearchParams, urlBuilder } from '@starjack/utils';
import { baseApi } from '@starjack/api';
import {
    VoucherTemplateAdminDTO,
    VoucherTemplateAdminDetailDTO,
    CreateVoucherTemplateCommand,
    UpdateVoucherTemplateCommand,
    SetActiveCommand,
    VoucherFontDTO,
} from '../model/voucherTemplate.types';

const urls = {
    templates: urlBuilder('order', '/admin/voucher-templates'),
    fonts: urlBuilder('order', '/admin/voucher-fonts'),
};

export const voucherTemplateApi = baseApi
    .enhanceEndpoints({ addTagTypes: ['VOUCHER_TEMPLATES', 'VOUCHER_FONTS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getVoucherTemplates: builder.query<
                VoucherTemplateAdminDTO[],
                { liftOperatorId?: string } | void
            >({
                query: (params) => {
                    const p = params || {};
                    const searchParams = buildSearchParams({
                        liftOperatorId: p.liftOperatorId,
                    });
                    return `${urls.templates}${searchParams}`;
                },
                providesTags: ['VOUCHER_TEMPLATES'],
            }),

            getVoucherTemplate: builder.query<
                VoucherTemplateAdminDetailDTO,
                string
            >({
                query: (id) => `${urls.templates}/${id}`,
                providesTags: ['VOUCHER_TEMPLATES'],
            }),

            postVoucherTemplate: builder.mutation<
                VoucherTemplateAdminDetailDTO,
                CreateVoucherTemplateCommand
            >({
                query: (body) => ({
                    url: urls.templates,
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['VOUCHER_TEMPLATES'],
            }),

            putVoucherTemplate: builder.mutation<
                VoucherTemplateAdminDetailDTO,
                { id: string; body: UpdateVoucherTemplateCommand }
            >({
                query: ({ id, body }) => ({
                    url: `${urls.templates}/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['VOUCHER_TEMPLATES'],
            }),

            deleteVoucherTemplate: builder.mutation<void, string>({
                query: (id) => ({
                    url: `${urls.templates}/${id}`,
                    method: 'DELETE',
                    body: undefined,
                }),
                invalidatesTags: ['VOUCHER_TEMPLATES'],
            }),

            patchVoucherTemplateActive: builder.mutation<
                void,
                { id: string; body: SetActiveCommand }
            >({
                query: ({ id, body }) => ({
                    url: `${urls.templates}/${id}/active`,
                    method: 'PATCH',
                    body,
                }),
                invalidatesTags: ['VOUCHER_TEMPLATES'],
            }),

            getVoucherFonts: builder.query<
                VoucherFontDTO[],
                { liftOperatorId?: string } | void
            >({
                query: (params) => {
                    const p = params || {};
                    const searchParams = buildSearchParams({
                        liftOperatorId: p.liftOperatorId,
                    });
                    return `${urls.fonts}${searchParams}`;
                },
                providesTags: ['VOUCHER_FONTS'],
            }),

            deleteVoucherFont: builder.mutation<void, string>({
                query: (id) => ({
                    url: `${urls.fonts}/${id}`,
                    method: 'DELETE',
                    body: undefined,
                }),
                invalidatesTags: ['VOUCHER_FONTS'],
            }),
        }),
    });

export const useLazyGetVoucherTemplateQuery =
    voucherTemplateApi.useLazyGetVoucherTemplateQuery;

export const {
    useGetVoucherTemplatesQuery,
    useGetVoucherTemplateQuery,
    usePostVoucherTemplateMutation,
    usePutVoucherTemplateMutation,
    useDeleteVoucherTemplateMutation,
    usePatchVoucherTemplateActiveMutation,
    useGetVoucherFontsQuery,
    useDeleteVoucherFontMutation,
} = voucherTemplateApi;
